import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { get } from "../api/client";

interface VacatureListItem {
  id: number;
  functie: string;
  partner_name?: string;
  locatie?: string | null;
  status: string;
  priority: string;
  bemiddeling_status: string;
  updated_at: string;
}

interface VacatureListModalProps {
  title: string;
  filterParams: string;
  myHmps: boolean;
  kamId?: number;
  onClose: () => void;
}

function statusPill(status: string) {
  const styles: Record<string, string> = {
    open: "bg-green-100 text-green-700",
    gesloten: "bg-slate-100 text-slate-600",
    on_hold: "bg-yellow-100 text-yellow-700",
  };
  const labels: Record<string, string> = {
    open: "Open",
    gesloten: "Gesloten",
    on_hold: "On Hold",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
        styles[status] ?? "bg-slate-100 text-slate-500"
      }`}
    >
      {labels[status] ?? status}
    </span>
  );
}

function priorityPill(priority: string) {
  const styles: Record<string, string> = {
    hoog: "bg-red-100 text-red-700",
    normaal: "bg-blue-100 text-blue-700",
    laag: "bg-slate-100 text-slate-500",
  };
  const labels: Record<string, string> = {
    hoog: "Hoog",
    normaal: "Normaal",
    laag: "Laag",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
        styles[priority] ?? "bg-slate-100 text-slate-500"
      }`}
    >
      {labels[priority] ?? priority}
    </span>
  );
}

function bemiddelingPill(status: string) {
  const styles: Record<string, string> = {
    geen: "bg-slate-100 text-slate-500",
    in_bemiddeling: "bg-waves-blue-light text-waves-blue",
    shortlisted: "bg-amber-100 text-amber-700",
    geplaatst: "bg-emerald-100 text-emerald-700",
  };
  const labels: Record<string, string> = {
    geen: "Geen",
    in_bemiddeling: "In Bemiddeling",
    shortlisted: "Shortlisted",
    geplaatst: "Geplaatst",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
        styles[status] ?? "bg-slate-100 text-slate-500"
      }`}
    >
      {labels[status] ?? status}
    </span>
  );
}

export default function VacatureListModal({
  title,
  filterParams,
  myHmps,
  kamId,
  onClose,
}: VacatureListModalProps) {
  const [vacatures, setVacatures] = useState<VacatureListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(filterParams);
    if (myHmps) params.set("my_hmps", "true");
    if (kamId) params.set("kam_id", String(kamId));
    get<VacatureListItem[]>(`/dashboard/kam/vacatures?${params.toString()}`)
      .then(setVacatures)
      .catch((err) => setError(err instanceof Error ? err.message : "Laden mislukt."))
      .finally(() => setLoading(false));
  }, [filterParams, myHmps, kamId]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal panel */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl mx-4 md:mx-auto flex flex-col"
           style={{ maxHeight: "85vh" }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 flex-shrink-0">
          <h3 className="font-semibold text-slate-800 text-lg">{title}</h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-2 border-waves-blue border-t-transparent rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="px-6 py-6">
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">
                <strong>Fout bij laden:</strong> {error}
              </div>
            </div>
          ) : vacatures.length === 0 ? (
            <div className="py-16 text-center text-slate-400 text-sm">
              Geen vacatures gevonden voor dit filter.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-50 z-10">
                  <tr className="border-b border-slate-100">
                    <th className="text-left py-3 px-5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Functie
                    </th>
                    <th className="text-left py-3 px-5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Partner
                    </th>
                    <th className="text-left py-3 px-5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Locatie
                    </th>
                    <th className="text-left py-3 px-5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Status
                    </th>
                    <th className="text-left py-3 px-5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Prioriteit
                    </th>
                    <th className="text-left py-3 px-5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Bemiddeling
                    </th>
                    <th className="text-left py-3 px-5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Laatste Update
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {vacatures.map((v) => (
                    <tr
                      key={v.id}
                      className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60"
                    >
                      <td className="py-3 px-5 font-medium text-slate-800">
                        {v.functie}
                      </td>
                      <td className="py-3 px-5 text-slate-500">
                        {v.partner_name ?? "—"}
                      </td>
                      <td className="py-3 px-5 text-slate-500">
                        {v.locatie ?? "—"}
                      </td>
                      <td className="py-3 px-5">{statusPill(v.status)}</td>
                      <td className="py-3 px-5">{priorityPill(v.priority)}</td>
                      <td className="py-3 px-5">{bemiddelingPill(v.bemiddeling_status)}</td>
                      <td className="py-3 px-5 text-slate-400 text-xs">
                        {new Date(v.updated_at).toLocaleDateString("nl-NL", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between flex-shrink-0">
          <span className="text-xs text-slate-400">
            {!loading && !error && `${vacatures.length} vacature${vacatures.length !== 1 ? "s" : ""} gevonden`}
          </span>
          <button onClick={onClose} className="btn-secondary min-h-[44px] md:min-h-0">
            Sluiten
          </button>
        </div>
      </div>
    </div>
  );
}
